import moment from 'moment';
import EventBus from 'eventing-bus';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Event as EventIcon, AccessTime as TimeIcon, Repeat as RepeatIcon } from '@mui/icons-material';
import { Box, Button, TextField, Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, Grid } from '@mui/material';

import ScheduleTriggerConfig from './WorkflowBuilder/components/ScheduleTriggerConfig';
import { getWorkflowSchedules, createWorkflowSchedule, updateWorkflowSchedule, deleteWorkflowSchedule, pauseWorkflowSchedule, resumeWorkflowSchedule } from '../../store/Workflows';

const SCHEDULE_TYPES = {
  once: { label: 'Run Once', icon: <EventIcon /> },
  recurring: { label: 'Recurring', icon: <RepeatIcon /> },
  cron: { label: 'Custom Cron', icon: <TimeIcon /> }
};

const RECURRENCE_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'hourly', label: 'Hourly' }
];

const WorkflowScheduler = ({ setScheduleDialog, scheduleDialog }) => {
  const { workflowId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState(0);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [scheduleData, setScheduleData] = useState({
    name: '',
    type: 'once',
    startTime: moment().add(1, 'hour'),
    endTime: null,
    recurrence: 'daily',
    cronExpression: '',
    enabled: true,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    input: {},
    maxExecutions: null,
    description: ''
  });

  const { workflowSchedules, schedulesLoading, singleWorkflow } = useSelector(state => state.Workflows);

  useEffect(() => {
    if (workflowId) {
      dispatch(getWorkflowSchedules(workflowId));
    }
  }, [dispatch, workflowId]);

  const handleCreateSchedule = () => {
    setSelectedSchedule(null);
    setScheduleData({
      name: '',
      type: 'once',
      startTime: moment().add(1, 'hour'),
      endTime: null,
      recurrence: 'daily',
      cronExpression: '',
      enabled: true,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      input: {},
      maxExecutions: null,
      description: ''
    });
    setScheduleDialog(true);
  };

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleData({
      name: schedule.name || '',
      type: schedule.type || 'once',
      startTime: moment(schedule.startTime),
      endTime: schedule.endTime ? moment(schedule.endTime) : null,
      recurrence: schedule.recurrence || 'daily',
      cronExpression: schedule.cronExpression || '',
      enabled: schedule.enabled !== false,
      timezone: schedule.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      input: schedule.input || {},
      maxExecutions: schedule.maxExecutions || null,
      description: schedule.description || ''
    });
    setScheduleDialog(true);
  };

  const handleSaveSchedule = () => {
    const data = {
      ...scheduleData,
      workflowId,
      startTime: scheduleData.startTime.toISOString(),
      endTime: scheduleData.endTime?.toISOString() || null
    };

    const successCallback = () => {
      setScheduleDialog(false);
      dispatch(getWorkflowSchedules(workflowId));
      EventBus.publish('success', selectedSchedule ? 'Schedule updated' : 'Schedule created');
    };

    const failCallback = () => {
      EventBus.publish('error', 'Failed to save schedule');
    };

    if (selectedSchedule) {
      dispatch(updateWorkflowSchedule({
        scheduleId: selectedSchedule._id,
        data,
        successCallback,
        failCallback
      }));
    } else {
      dispatch(createWorkflowSchedule({
        data,
        successCallback,
        failCallback
      }));
    }
  };

  const handleDeleteSchedule = (schedule) => {
    if (window.confirm(`Are you sure you want to delete schedule "${schedule.name}"?`)) {
      dispatch(deleteWorkflowSchedule({
        scheduleId: schedule._id,
        successCallback: () => {
          dispatch(getWorkflowSchedules(workflowId));
          EventBus.publish('success', 'Schedule deleted');
        },
        failCallback: () => {
          EventBus.publish('error', 'Failed to delete schedule');
        }
      }));
    }
  };

  const handleToggleSchedule = (schedule) => {
    const action = schedule.enabled ? pauseWorkflowSchedule : resumeWorkflowSchedule;
    dispatch(action({
      scheduleId: schedule._id,
      successCallback: () => {
        dispatch(getWorkflowSchedules(workflowId));
        EventBus.publish('success', `Schedule ${schedule.enabled ? 'paused' : 'resumed'}`);
      },
      failCallback: () => {
        EventBus.publish('error', 'Failed to update schedule');
      }
    }));
  };

  const getNextRunTime = (schedule) => {
    if (!schedule.enabled) return 'Paused';
    if (schedule.type === 'once' && moment(schedule.startTime).isBefore(moment())) {
      return 'Completed';
    }
    // This would typically be calculated on the backend
    return moment(schedule.nextRun || schedule.startTime).format('MMM DD, YYYY HH:mm');
  };

  const getScheduleStatus = (schedule) => {
    if (!schedule.enabled) return { label: 'Paused', color: 'default' };
    if (schedule.type === 'once' && moment(schedule.startTime).isBefore(moment())) {
      return { label: 'Completed', color: 'success' };
    }
    if (moment().isBetween(schedule.startTime, schedule.endTime)) {
      return { label: 'Active', color: 'primary' };
    }
    return { label: 'Scheduled', color: 'info' };
  };

  const generateCronDescription = (cronExpression) => {
    // Simple cron description - in production, use a cron parser library
    const parts = cronExpression.split(' ');
    if (parts.length !== 5) return 'Invalid cron expression';

    const [minute, hour, day, month, weekday] = parts;

    if (cronExpression === '0 9 * * 1-5') return 'Every weekday at 9:00 AM';
    if (cronExpression === '0 0 * * 0') return 'Every Sunday at midnight';
    if (cronExpression === '0 */6 * * *') return 'Every 6 hours';

    return cronExpression; // Fallback to raw expression
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ p: 3 }}>
        {/* <div className='workflow-schedulerModal'>
        <div className='title-area'>
          {singleWorkflow && (
            <div className='left-area'>
              <h4>{singleWorkflow.name}</h4>
              <p>{singleWorkflow.description || 'No description'}</p>
            </div>
          )}
          <button className='btn-style-border' onClick={handleCreateSchedule}>
            +  Create Schedule
          </button>
        </div>
        <div className='workflow-infor-sch'>
            <div>

            </div>
        </div>
      </div> */}
        {/* Header */}
        {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => history.goBack()}>
              <BackIcon />
            </IconButton>
            <Typography variant="h5">
              Workflow Scheduler
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateSchedule}
          >
            Create Schedule
          </Button>
        </Box> */}

        {/* Workflow Info */}
        {/* {singleWorkflow && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {singleWorkflow.name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {singleWorkflow.description || 'No description'}
            </Typography>
          </Paper>
        )} */}

        {/* Tabs */}
        {/* <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)}>
            <Tab label="Schedules" icon={<ScheduleIcon />} iconPosition="start" />
            <Tab label="Execution History" icon={<CalendarIcon />} iconPosition="start" />
          </Tabs>
        </Paper> */}

        {/* Content */}
        {/* {activeTab === 0 && (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Schedule</TableCell>
                    <TableCell>Next Run</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workflowSchedules?.map((schedule) => {
                    const status = getScheduleStatus(schedule);
                    return (
                      <TableRow key={schedule._id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {schedule.name}
                          </Typography>
                          {schedule.description && (
                            <Typography variant="caption" color="textSecondary">
                              {schedule.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={SCHEDULE_TYPES[schedule.type]?.label || schedule.type}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {schedule.type === 'once' && (
                            <Typography variant="body2">
                              {moment(schedule.startTime).format('MMM DD, YYYY HH:mm')}
                            </Typography>
                          )}
                          {schedule.type === 'recurring' && (
                            <Typography variant="body2">
                              Every {schedule.recurrence}
                            </Typography>
                          )}
                          {schedule.type === 'cron' && (
                            <Typography variant="body2">
                              {generateCronDescription(schedule.cronExpression)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {getNextRunTime(schedule)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={status.label}
                            size="small"
                            color={status.color}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleSchedule(schedule)}
                            >
                              {schedule.enabled ? <PauseIcon /> : <PlayIcon />}
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEditSchedule(schedule)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteSchedule(schedule)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(!workflowSchedules || workflowSchedules.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Box sx={{ py: 4 }}>
                          <Typography variant="body2" color="textSecondary">
                            No schedules configured. Create your first schedule to automate workflow execution.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
        
        {activeTab === 1 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Scheduled Execution History
            </Typography>
            <Alert severity="info">
              Execution history for scheduled workflow runs will be displayed here.
            </Alert>
          </Paper>
        )} */}

        {/* Create/Edit Schedule Dialog */}

        <Modal isOpen={scheduleDialog} className={`main-modal new-user-modal modal-dialog-centered workflow-modal`}>
          <ModalHeader toggle={() => setScheduleDialog(false)}>
            <h3>{selectedSchedule ? 'Edit Schedule' : 'Create Schedule'}</h3>
          </ModalHeader>
          <ModalBody>
            <div className='content-area'>
              <div className='form-area'>
                <ScheduleTriggerConfig
                  // value={config}
                  // onChange={(scheduleConfig) => setConfig({ ...config, ...scheduleConfig })}
                />
                {/* <div className='row'>
                  <div className='col-lg-6 col-ms-12'>
                    <div className='group-form'>
                      <label>Schedule Name</label>
                      <TextField
                        placeholder="Add schedule name"
                        fullWidth
                        value={scheduleData.name}
                        onChange={(e) => setScheduleData(prev => ({ ...prev, name: e.target.value }))}
                        required
                      />
                    </div>
                  </div>
                  <div className='col-lg-6 col-ms-12'>
                    <div className='group-form'>
                      <label>Schedule Type</label>
                      <FormControl fullWidth>
                        <Select
                          value={scheduleData.type}
                          onChange={(e) => setScheduleData(prev => ({ ...prev, type: e.target.value }))}
                          placeholder="Schedule Type"
                        >
                          {Object.entries(SCHEDULE_TYPES).map(([value, config]) => (
                            <MenuItem key={value} value={value}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {config.icon}
                                {config.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                </div> */}

                <div className='btns-box text-right'>
                  <button className='btn-style-border' onClick={() => setScheduleDialog(false)}>Cancel</button>
                  <button className='btn-style-new'  >Save</button>
                </div>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Dialog maxWidth="md" fullWidth>
          <DialogTitle>

          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>

                </Grid>

                <Grid item xs={12} md={6}>

                </Grid>

                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    label="Start Time"
                    value={scheduleData.startTime}
                    onChange={(value) => setScheduleData(prev => ({ ...prev, startTime: value }))}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>

                {scheduleData.type === 'recurring' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Recurrence</InputLabel>
                        <Select
                          value={scheduleData.recurrence}
                          onChange={(e) => setScheduleData(prev => ({ ...prev, recurrence: e.target.value }))}
                          label="Recurrence"
                        >
                          {RECURRENCE_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <DateTimePicker
                        label="End Time (Optional)"
                        value={scheduleData.endTime}
                        onChange={(value) => setScheduleData(prev => ({ ...prev, endTime: value }))}
                        renderInput={(params) => <TextField {...params} fullWidth />}
                      />
                    </Grid>
                  </>
                )}

                {scheduleData.type === 'cron' && (
                  <Grid item xs={12}>
                    <TextField
                      label="Cron Expression"
                      fullWidth
                      value={scheduleData.cronExpression}
                      onChange={(e) => setScheduleData(prev => ({ ...prev, cronExpression: e.target.value }))}
                      placeholder="0 9 * * 1-5 (weekdays at 9 AM)"
                      helperText="Use standard cron format: minute hour day month weekday"
                    />
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Max Executions (Optional)"
                    type="number"
                    fullWidth
                    value={scheduleData.maxExecutions || ''}
                    onChange={(e) => setScheduleData(prev => ({
                      ...prev,
                      maxExecutions: e.target.value ? parseInt(e.target.value) : null
                    }))}
                    helperText="Leave empty for unlimited"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={scheduleData.enabled}
                        onChange={(e) => setScheduleData(prev => ({ ...prev, enabled: e.target.checked }))}
                      />
                    }
                    label="Enable Schedule"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={2}
                    value={scheduleData.description}
                    onChange={(e) => setScheduleData(prev => ({ ...prev, description: e.target.value }))}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSchedule}
              variant="contained"
              disabled={!scheduleData.name.trim()}
            >
              {selectedSchedule ? 'Update' : 'Create'} Schedule
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default WorkflowScheduler;